import type { IntakeMarketConfig, MarketIntakeIssue, MarketIntakeSnapshot, MarketIntakeValues } from "./types.js";
import { dbk_normalizeUsEstimateFields, dbk_normalizeUsEstimateValues } from "./usEstimateContract.js";
function record(value: unknown): value is Record<string, unknown> { return !!value && typeof value === "object" && !Array.isArray(value); }
export function normalizeUsBusinessPhone(value: string): string | null {
  if (!value.trim()) return "";
  const match = value.trim().match(/^(.*?)(?:\s*(?:ext\.?|x|;ext=)\s*(\d{1,6}))?$/i);
  if (!match || !/^[+()\d\s.-]+$/.test(match[1] ?? "")) return null;
  let digits = (match[1] ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
  return `+1${digits}${match[2] ? `;ext=${match[2]}` : ""}`;
}
export function validateMarketIntake(payload: unknown, config: IntakeMarketConfig):
  { ok: true; snapshot: MarketIntakeSnapshot } | { ok: false; issues: MarketIntakeIssue[] } {
  const issues: MarketIntakeIssue[] = [];
  const add = (field: string, code: string, message: string) => issues.push({ field, code, message });
  if (!record(payload)) return { ok: false, issues: [{ field: "$", code: "object", message: "Provide a request object." }] };
  for (const key of Object.keys(payload)) if (!["country_code","config_version","schema_version","locale","fields","consent","tax_identifier_provided"].includes(key))
    add("$","unknown_field","This field is not part of this market's intake.");
  if (payload.country_code !== config.country_code) add("country_code","market_mismatch","Choose the correct country intake.");
  if (payload.config_version !== config.config_version || payload.schema_version !== config.schema_version)
    add("config_version","version_mismatch","Refresh the form to use the current configuration.");
  if (payload.locale !== config.default_locale) add("locale","unsupported_locale","This draft uses the market's configured display language.");
  const supplied = record(payload.fields) ? (config.country_code === "US" ? dbk_normalizeUsEstimateValues(payload.fields, config) : payload.fields) : {};
  if (config.country_code === "US") config = { ...config, fields: dbk_normalizeUsEstimateFields(config.fields) };
  if (!record(payload.fields)) add("fields","object","Provide the intake fields.");
  const known = new Set(config.fields.map(field => field.key));
  for (const key of Object.keys(supplied)) if (!known.has(key)) add("fields","unknown_field","This field is not part of this market's intake.");
  const fields: MarketIntakeValues = {};
  let taxIdentifierProvided = payload.tax_identifier_provided === true;
  if (payload.tax_identifier_provided !== undefined && typeof payload.tax_identifier_provided !== "boolean") add("tax_identifier_provided","boolean","Provide a boolean.");
  for (const field of config.fields) {
    const raw = supplied[field.key];
    if (field.kind === "multiselect") {
      const values = raw === undefined ? [] : raw;
      const allowed = new Set(field.options?.map(option => option.value) ?? []);
      if (!Array.isArray(values) || values.some(v => typeof v !== "string" || !allowed.has(v)) || new Set(values).size !== values.length)
        add(field.key,"options","Choose the listed options.");
      else if (field.required && !values.length) add(field.key,"required","Choose an option.");
      else fields[field.key] = values;
      continue;
    }
    if (raw !== undefined && typeof raw !== "string") { add(field.key,"text","Provide text."); continue; }
    const value = typeof raw === "string" ? raw.trim() : "";
    if (field.required && !value) add(field.key,"required","This field is required.");
    if (value.length > (field.maxLength ?? 500) || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(value))
      add(field.key,"length","Use a shorter value without control characters.");
    if (field.kind !== "ein" && /\b(?:\d{2}-\d{7}|\d{3}-\d{2}-\d{4})\b/.test(value))
      add(field.key,"tax_identifier_not_supported","Do not include tax identifiers in this field.");
    if (field.kind === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) add(field.key,"email","Enter a valid email address.");
    if (field.kind === "select" && value && !field.options?.some(option => option.value === value)) add(field.key,"options","Choose a listed option.");
    if (field.kind === "integer" && value && !/^(?:0|[1-9]\d{0,5}|not_sure)$/.test(value))
      add(field.key,"integer","Enter a whole number from 0 to 999999, or enter not_sure.");
    let normalized = value;
    if (field.kind === "phone" && value) {
      const phone = normalizeUsBusinessPhone(value);
      if (phone === null) add(field.key,"phone","Enter a 10-digit number, optionally with +1 and an extension.");
      else normalized = phone;
    }
    if (field.kind === "ein" && value) {
      taxIdentifierProvided = true;
      if (!/^\d{2}-?\d{7}$/.test(value)) add(field.key,"ein_format","Use nine digits, optionally with a hyphen after the first two.");
    }
    // Sensitive identifiers never enter persisted snapshots, draft proposals or response bodies.
    if (!field.sensitive && field.kind !== "ein" && !/^(?:ein|tin|tax_identifier|ssn|itin)$/i.test(field.key)) fields[field.key] = normalized;
  }
  if (!record(payload.consent) || payload.consent.privacy_acknowledged !== true || payload.consent.terms_accepted !== true)
    add("consent","required","Confirm the privacy notice and terms.");
  if (issues.length) return { ok: false, issues };
  return { ok: true, snapshot: {
    country_code: config.country_code, config_version: config.config_version, schema_version: config.schema_version,
    locale: config.default_locale, currency: config.currency, fields, tax_identifier_provided: taxIdentifierProvided,
    config_snapshot: structuredClone(config)
  } };
}
